import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { flashcardAPI } from '../services/api';

const FlashcardDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [flashcard, setFlashcard] = useState(null);
  const [terms, setTerms] = useState([]);
  const [currentTermIndex, setCurrentTermIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadFlashcard();
  }, [id]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Prevent default for arrow keys to avoid page scrolling
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', ' '].includes(e.key)) {
        e.preventDefault();
      }

      switch(e.key) {
        case 'ArrowLeft':
          handlePrevious();
          break;
        case 'ArrowRight':
          handleNext();
          break;
        case ' ':
        case 'ArrowUp':
        case 'ArrowDown':
          handleFlip();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentTermIndex, terms.length, isFlipped]);

  const loadFlashcard = async () => {
    try {
      setLoading(true);
      const response = await flashcardAPI.getFlashcard(id);
      setFlashcard(response.data.flashCard);
      setTerms(response.data.terms);
      setCurrentTermIndex(0);
      setIsFlipped(false);
      setError('');
    } catch (err) {
      setError('Failed to load flashcard');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const handleNext = () => {
    if (currentTermIndex < terms.length - 1) {
      setCurrentTermIndex(currentTermIndex + 1);
      setIsFlipped(false);
    }
  };

  const handlePrevious = () => {
    if (currentTermIndex > 0) {
      setCurrentTermIndex(currentTermIndex - 1);
      setIsFlipped(false);
    }
  };

  const handleShuffle = () => {
    const shuffled = [...terms].sort(() => Math.random() - 0.5);
    setTerms(shuffled);
    setCurrentTermIndex(0);
    setIsFlipped(false);
  };

  const handleReset = () => {
    setCurrentTermIndex(0);
    setIsFlipped(false);
  };

  const handleBack = () => {
    navigate('/flashcards');
  };

  const handleStartMatch = () => {
    navigate(`/flashcards/${id}/match`);
  };

  if (loading) {
    return <div className="loading">Loading flashcard...</div>;
  }

  if (error) {
    return (
      <div className="container">
        <div className="card">
          <p style={{ color: '#f56565', marginBottom: '16px' }}>{error}</p>
          <button 
            onClick={handleBack}
            className="btn btn-secondary"
          >
            ← Back to Flashcards
          </button>
        </div>
      </div>
    );
  }

  if (!flashcard || terms.length === 0) {
    return (
      <div className="container">
        <div className="card">
          <p>No terms found in this flashcard.</p>
          <button 
            onClick={handleBack}
            className="btn btn-secondary"
          >
            ← Back to Flashcards
          </button>
        </div>
      </div>
    );
  }

  const currentTerm = terms[currentTermIndex];
  const progress = ((currentTermIndex + 1) / terms.length) * 100;

  return (
    <div style={{
      maxWidth: '800px',
      margin: '0 auto',
      padding: '24px'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '24px',
        padding: '16px 20px',
        backgroundColor: 'white',
        borderRadius: '8px',
        border: '1px solid #e2e8f0'
      }}>
        <div>
          <h2 style={{ color: '#667eea', fontSize: '22px', marginBottom: '4px' }}>{flashcard.title}</h2>
          <p style={{ color: '#718096', fontSize: '13px', margin: 0 }}>
            Study Mode - {terms.length} terms
          </p>
        </div>
        <button 
          onClick={handleBack}
          style={{
            padding: '10px 20px',
            backgroundColor: '#f56565',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600'
          }}
        >
          Exit Study Mode
        </button>
      </div>
      
      <div style={{
        marginBottom: '24px',
        backgroundColor: 'white',
        padding: '16px',
        borderRadius: '8px',
        border: '1px solid #e2e8f0'
      }}>
        <div style={{
          width: '100%',
          height: '8px',
          backgroundColor: '#e2e8f0',
          borderRadius: '4px',
          overflow: 'hidden',
          marginBottom: '8px'
        }}>
          <div style={{
            width: `${progress}%`,
            height: '100%',
            backgroundColor: '#667eea',
            transition: 'width 0.3s ease'
          }}></div>
        </div>
        <span style={{
          fontSize: '14px',
          color: '#4a5568',
          fontWeight: '600'
        }}>
          {currentTermIndex + 1} / {terms.length}
        </span>
      </div>

      {currentTerm && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '24px',
          perspective: '1000px'
        }}>
          <div 
            onClick={handleFlip}
            style={{
              width: '100%',
              maxWidth: '600px',
              height: '350px',
              position: 'relative',
              transformStyle: 'preserve-3d',
              transition: 'transform 0.6s',
              transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0)',
              cursor: 'pointer'
            }}
          >
            {/* Front */}
            <div style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              backfaceVisibility: 'hidden',
              backgroundColor: 'white',
              borderRadius: '12px',
              border: '2px solid #667eea',
              padding: '32px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '12px',
                fontWeight: '600',
                color: '#667eea',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                marginBottom: '16px'
              }}>
                Term
              </div>
              <div style={{
                fontSize: '24px',
                fontWeight: '600',
                color: '#2d3748',
                marginBottom: '24px'
              }}>
                {currentTerm.term}
              </div>
              <div style={{
                fontSize: '13px',
                color: '#a0aec0',
                fontStyle: 'italic'
              }}>
                Click to reveal definition
              </div>
            </div>
            
            {/* Back */}
            <div style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              backfaceVisibility: 'hidden',
              backgroundColor: 'white',
              borderRadius: '12px',
              border: '2px solid #48bb78',
              padding: '32px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              transform: 'rotateY(180deg)'
            }}>
              <div style={{
                fontSize: '12px',
                fontWeight: '600',
                color: '#48bb78',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                marginBottom: '16px'
              }}>
                Definition
              </div>
              <div style={{
                fontSize: '18px',
                color: '#2d3748',
                lineHeight: '1.6',
                marginBottom: '24px'
              }}>
                {currentTerm.definition}
              </div>
              <div style={{
                fontSize: '13px',
                color: '#a0aec0',
                fontStyle: 'italic'
              }}>
                Click to see term
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{
        display: 'flex',
        gap: '12px',
        justifyContent: 'center',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={handlePrevious}
          disabled={currentTermIndex === 0}
          style={{
            padding: '10px 20px',
            backgroundColor: currentTermIndex === 0 ? '#cbd5e0' : '#667eea',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: currentTermIndex === 0 ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            opacity: currentTermIndex === 0 ? 0.6 : 1
          }}
        >
          ← Previous
        </button>
        <button 
          onClick={handleShuffle}
          style={{
            padding: '10px 20px',
            backgroundColor: '#ed8936',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600'
          }}
        >
          🔀 Shuffle
        </button>
        <button 
          onClick={handleStartMatch}
          style={{
            padding: '10px 20px',
            backgroundColor: '#38b2ac',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#319795'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#38b2ac'}
        >
          🎮 Play Match
        </button>
        <button 
          onClick={handleReset}
          style={{
            padding: '10px 20px',
            backgroundColor: '#4299e1',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600'
          }}
        >
          ↺ Reset
        </button>
        <button
          onClick={handleNext}
          disabled={currentTermIndex === terms.length - 1}
          style={{
            padding: '10px 20px',
            backgroundColor: currentTermIndex === terms.length - 1 ? '#cbd5e0' : '#667eea',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: currentTermIndex === terms.length - 1 ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            opacity: currentTermIndex === terms.length - 1 ? 0.6 : 1
          }}
        >
          Next →
        </button>
      </div>
    </div>
  );
};

export default FlashcardDetail;
